import { Plus, Trash2, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { TacticalSpecialist, PerformanceKicker, KickerEligiblePerson } from '@/types/project';
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../utils';
import { KICKER_TIERS, STRATEGY_PARTNERS } from '../constants';
import { TierBadge } from '../shared/TierBadge';
import { BonusRecipientPill } from '../shared/BonusRecipientPill';
import { useState } from 'react';

interface KickerCalculation {
  id: string;
  name: string;
  bonus: number;
  specialistPayout: number;
  perPersonPayout: number;
  eligibleCount: number;
  ownerPayout: number;
  tierInfo: typeof KICKER_TIERS[0] | undefined;
}

interface PerformanceBonusesTabProps {
  performanceKickers: PerformanceKicker[];
  addPerformanceKicker: () => void;
  removePerformanceKicker: (id: string) => void;
  updatePerformanceKicker: (id: string, field: keyof PerformanceKicker, value: any) => void;
  tacticalSpecialists: TacticalSpecialist[];
  operationsOwners: string[];
  kickerCalculations: KickerCalculation[];
  totalKickerBonus: number;
  totalKickerOwnerRevenue: number;
  totalKickerSpecialistPayouts: number;
}

export function PerformanceBonusesTab({
  performanceKickers,
  addPerformanceKicker,
  removePerformanceKicker,
  updatePerformanceKicker,
  tacticalSpecialists,
  operationsOwners,
  kickerCalculations,
  totalKickerBonus,
  totalKickerOwnerRevenue,
  totalKickerSpecialistPayouts,
}: PerformanceBonusesTabProps) {
  const availableSpecialists = tacticalSpecialists.filter(spec => spec.handlesKickers);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Summary Bar */}
      <Card className="bg-gradient-to-r from-[rgba(27,58,45,0.05)] to-[rgba(184,150,62,0.07)]">
        <CardContent className="py-3 md:py-4">
          <div className="grid grid-cols-3 gap-2 md:gap-4 text-center">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Total Potential</p>
              <p className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold font-serif text-info">
                {formatCurrency(totalKickerBonus)}
              </p>
            </div>
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Owner Share</p>
              <p className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold font-serif text-positive">
                {formatCurrency(totalKickerOwnerRevenue)}
              </p>
            </div>
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Specialist Share</p>
              <p className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold font-serif text-primary">
                {formatCurrency(totalKickerSpecialistPayouts)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Kicker Cards */}
      {performanceKickers.map((kicker, index) => {
        const calculation = kickerCalculations[index];
        const specialistProgress = calculation && calculation.bonus > 0
          ? (calculation.specialistPayout / calculation.bonus) * 100
          : 0;

        return (
          <KickerCard
            key={kicker.id}
            kicker={kicker}
            index={index}
            calculation={calculation}
            specialistProgress={specialistProgress}
            availableSpecialists={availableSpecialists}
            operationsOwners={operationsOwners}
            onUpdate={updatePerformanceKicker}
            onRemove={removePerformanceKicker}
          />
        );
      })}

      {/* Add Kicker Button */}
      <Button
        variant="outline"
        className="w-full py-6 border-dashed"
        onClick={addPerformanceKicker}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Performance Bonus
      </Button>
    </div>
  );
}

interface KickerCardProps {
  kicker: PerformanceKicker;
  index: number;
  calculation: KickerCalculation | undefined;
  specialistProgress: number;
  availableSpecialists: TacticalSpecialist[];
  operationsOwners: string[];
  onUpdate: (id: string, field: keyof PerformanceKicker, value: any) => void;
  onRemove: (id: string) => void;
}

function KickerCard({
  kicker,
  index,
  calculation,
  specialistProgress,
  availableSpecialists,
  operationsOwners,
  onUpdate,
  onRemove,
}: KickerCardProps) {
  const [personSelectorOpen, setPersonSelectorOpen] = useState(false);
  const [customNameInput, setCustomNameInput] = useState('');

  // Get eligible people from new format or fall back to empty array
  const eligiblePeople = kicker.eligiblePeople || [];
  const sharePercentage = kicker.sharePercentage ?? kicker.specialistSharePercentage ?? 10;

  // Build suggestions from project team (specialists + ops owners)
  const getSuggestions = (): Array<{ name: string; role?: string; isOwner: boolean }> => {
    const suggestions: Array<{ name: string; role?: string; isOwner: boolean }> = [];

    // Add operations owners
    operationsOwners.forEach(owner => {
      if (!eligiblePeople.some(p => p.name === owner)) {
        suggestions.push({
          name: owner,
          role: 'Operations',
          isOwner: STRATEGY_PARTNERS.includes(owner)
        });
      }
    });

    // Add tactical specialists (using assignedOwner or name)
    availableSpecialists.forEach(spec => {
      const payeeName = spec.assignedOwner || spec.name;
      if (!eligiblePeople.some(p => p.name === payeeName) && !suggestions.some(s => s.name === payeeName)) {
        suggestions.push({
          name: payeeName,
          role: spec.name,
          isOwner: STRATEGY_PARTNERS.includes(payeeName)
        });
      }
    });

    return suggestions;
  };

  const addPerson = (name: string, role?: string) => {
    const isOwner = STRATEGY_PARTNERS.includes(name);
    const newPerson: KickerEligiblePerson = {
      name,
      type: isOwner ? 'owner' : 'external',
      role
    };
    const newEligiblePeople = [...eligiblePeople, newPerson];
    onUpdate(kicker.id, 'eligiblePeople', newEligiblePeople);
  };

  const removePerson = (name: string) => {
    const newEligiblePeople = eligiblePeople.filter(p => p.name !== name);
    onUpdate(kicker.id, 'eligiblePeople', newEligiblePeople);
  };

  const handleCustomNameAdd = () => {
    const trimmedName = customNameInput.trim();
    if (trimmedName && !eligiblePeople.some(p => p.name === trimmedName)) {
      addPerson(trimmedName);
      setCustomNameInput('');
    }
  };

  return (
    <Card>
      <CardHeader className="px-3 sm:px-6 pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-base md:text-lg">
              {kicker.name || `Bonus #${index + 1}`}
            </CardTitle>
            <TierBadge tier={kicker.selectedTier} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(kicker.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6 space-y-4">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <div>
            <Label>Bonus Name</Label>
            <Input
              placeholder="Enter name"
              value={kicker.name}
              onChange={(e) => onUpdate(kicker.id, 'name', e.target.value)}
            />
          </div>
          <div>
            <Label>Marketing Spend</Label>
            <Input
              type="text"
              placeholder="$100,000"
              value={kicker.marketingSpend > 0 ? formatCurrencyInput(kicker.marketingSpend) : ''}
              onChange={(e) => onUpdate(kicker.id, 'marketingSpend', parseCurrencyInput(e.target.value))}
            />
          </div>
          <div>
            <Label>Performance Tier</Label>
            <Select
              value={kicker.selectedTier}
              onValueChange={(value) => onUpdate(kicker.id, 'selectedTier', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KICKER_TIERS.map((tier) => (
                  <SelectItem key={tier.value} value={tier.value}>
                    <span className="flex items-center gap-2">
                      <span>{tier.label}</span>
                      <span className="text-xs text-muted-foreground">{tier.description}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Who Receives This Bonus - Unified Person Picker */}
        <div className="space-y-3 pt-2 border-t">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Who Receives This Bonus</Label>
            <Popover open={personSelectorOpen} onOpenChange={setPersonSelectorOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  <Plus className="h-3 w-3 mr-1" />
                  Add Person
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="end">
                <Command>
                  <CommandInput
                    placeholder="Search or type a name..."
                    value={customNameInput}
                    onValueChange={setCustomNameInput}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {customNameInput.trim() ? (
                        <button
                          className="w-full p-2 text-left text-sm hover:bg-accent"
                          onClick={() => {
                            handleCustomNameAdd();
                            setPersonSelectorOpen(false);
                          }}
                        >
                          Add "{customNameInput.trim()}" as external contractor
                        </button>
                      ) : (
                        'Type a name to add or search team members'
                      )}
                    </CommandEmpty>
                    <CommandGroup heading="Project Team">
                      {getSuggestions().map((suggestion) => (
                        <CommandItem
                          key={`${suggestion.name}-${suggestion.role}`}
                          onSelect={() => {
                            addPerson(suggestion.name, suggestion.role);
                            setPersonSelectorOpen(false);
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <span>{suggestion.name}</span>
                            {suggestion.role && (
                              <span className="text-xs text-muted-foreground">
                                ({suggestion.role})
                              </span>
                            )}
                            <Badge
                              variant="outline"
                              className={`ml-auto text-xs ${
                                suggestion.isOwner
                                  ? 'border-success text-success'
                                  : 'border-warning text-warning'
                              }`}
                            >
                              {suggestion.isOwner ? 'Owner' : 'External'}
                            </Badge>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Quick Add Strategy Partners */}
          <div className="flex flex-wrap gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Quick add:</span>
            {STRATEGY_PARTNERS.map((owner) => {
              const isSelected = eligiblePeople.some(p => p.name === owner);
              return (
                <Button
                  key={owner}
                  variant={isSelected ? "secondary" : "outline"}
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => isSelected ? removePerson(owner) : addPerson(owner)}
                >
                  {owner}
                </Button>
              );
            })}
          </div>

          {/* Selected People Pills */}
          <div className="flex flex-wrap gap-2 min-h-[2rem]">
            {eligiblePeople.length > 0 ? (
              eligiblePeople.map((person) => (
                <BonusRecipientPill
                  key={person.name}
                  name={person.name}
                  role={person.role}
                  amount={calculation?.perPersonPayout || 0}
                  type={person.type}
                  onRemove={() => removePerson(person.name)}
                />
              ))
            ) : (
              <span className="text-sm text-muted-foreground italic">
                No recipients selected. Add people above to receive this bonus.
              </span>
            )}
          </div>
        </div>

        {/* Share Percentage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Share Per Person</Label>
            <span className="text-sm font-medium">{sharePercentage}% each</span>
          </div>
          <Slider
            min={0}
            max={100}
            step={5}
            value={[sharePercentage]}
            onValueChange={(value) => onUpdate(kicker.id, 'sharePercentage', value[0])}
          />
          <p className="text-xs text-muted-foreground">
            Each eligible person receives {sharePercentage}% of the bonus
          </p>
        </div>

        {/* Bonus Summary */}
        {calculation && calculation.bonus > 0 && (
          <div className="bg-muted/50 p-3 md:p-4 rounded-lg space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 text-center">
              <div>
                <div className="text-base md:text-lg lg:text-xl font-bold font-serif text-positive">
                  {formatCurrency(calculation.bonus)}
                </div>
                <div className="text-xs text-muted-foreground">Total Bonus</div>
              </div>
              <div>
                <div className="text-base md:text-lg lg:text-xl font-bold font-serif">
                  {calculation.eligibleCount || 0}
                </div>
                <div className="text-xs text-muted-foreground">Eligible</div>
              </div>
              <div>
                <div className="text-base md:text-lg lg:text-xl font-bold font-serif">
                  {formatCurrency(calculation.perPersonPayout || 0)}
                </div>
                <div className="text-xs text-muted-foreground">Per Person</div>
              </div>
              <div>
                <div className="text-base md:text-lg lg:text-xl font-bold font-serif text-positive">
                  {formatCurrency(calculation.ownerPayout)}
                </div>
                <div className="text-xs text-muted-foreground">To Owners</div>
              </div>
            </div>

            {/* Distribution Progress Bar */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Specialist Share: {formatCurrency(calculation.specialistPayout)}</span>
                <span>Owner Share: {formatCurrency(calculation.ownerPayout)}</span>
              </div>
              <div className="h-2 bg-success/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-info"
                  style={{ width: `${Math.min(specialistProgress, 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
